import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';

export interface CredentialInfo {
  /** Display name (seller or sub-seller name) */
  name: string;
  username: string;
  tempPassword: string;
}

export function CredentialDialog({
  credential,
  onClose,
}: {
  credential: CredentialInfo;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = t('sellers.credentialCopyFormat', {
      name: credential.name,
      username: credential.username,
      password: credential.tempPassword,
    });
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('sellers.credentialTitle')}
        </h3>

        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('sellers.credentialWarning')}
        </div>

        <div className="mb-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('sellers.name')}
            </label>
            <div className="font-medium text-gray-900 dark:text-gray-100">{credential.name}</div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('auth.username')}
            </label>
            <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm dark:bg-gray-700 dark:text-gray-100">
              {credential.username}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('sellers.tempPassword')}
            </label>
            <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm font-bold dark:bg-gray-700 dark:text-gray-100">
              {credential.tempPassword}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} className="flex-1">
            {copied ? t('sellers.copied') : t('sellers.copyCredentials')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
